import React, { FC, useMemo } from 'react';
import Image from 'next/image';
import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import { useGammaVaults } from '@app/hooks/gamma/useGammaVaults';
import { getStrategyAcronim } from '@app/helpers/vaults';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';

interface Props {
  horizontal?: boolean;
}

export const StrategyDetails: FC<Props> = ({ horizontal }) => {
  const { data: vaults } = useGammaVaults();
  const { currencyA, currencyB, strategy, mode, provider } =
    useLiquidityWizardState();

  const vault = useMemo(() => {
    if (!vaults) {
      return;
    }

    return vaults.find(
      item => item.id?.toLowerCase() === strategy?.toLowerCase()
    );
  }, [strategy, vaults]);

  const strategyName = getStrategyAcronim(vault?.name ?? '');

  const details = getStrategyDetails(strategyName);

  if (mode !== 'auto' || provider !== 'gamma' || !currencyA || !currencyB) {
    return null;
  }

  return (
    <Box w="100%" backgroundColor="brand.black" borderRadius={12}>
      <HStack
        bg="gray.2"
        borderRadius={4}
        w="100%"
        p={4}
        minWidth="100%"
        alignItems="baseline"
        gap={4}
      >
        <Image
          src={'/assets/images/providers/gamma.svg'}
          height={30}
          width={100}
          alt="Strategy"
        />
        <Text color="neutral.200" fontWeight={500} fontSize={18}>
          {strategyName}
        </Text>
      </HStack>
      <VStack
        width="100%"
        alignItems="flex-start"
        sx={{
          flexDirection: horizontal ? 'row' : 'column'
        }}
      >
        {vault && (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              // paddingBottom: horizontal ? 'unset' : '52%',
              // height: horizontal ? 200 : 185,
              my: 4
            }}
          >
            <img
              src={details.url}
              alt="Strategy"
              style={{ objectFit: 'contain', width: '100%' }}
            />
          </Box>
        )}

        <VStack alignItems="flex-start" width="100%" py={2}>
          <Text
            color="neutral.200"
            fontSize={14}
            padding={3}
            backgroundColor="neutral.800"
            borderRadius={8}
            lineHeight={1.4}
          >
            {details.desc}
          </Text>

          <Text
            color="brand.white"
            fontSize={24}
            fontWeight={700}
            lineHeight={1.4}
            mt={3}
            pl={2}
          >
            Benefits
          </Text>
          <Text
            color="neutral.200"
            fontSize={14}
            padding={3}
            backgroundColor="neutral.800"
            borderRadius={8}
            lineHeight={1.4}
          >
            {details.benefits}
          </Text>

          <Text
            color="brand.white"
            fontSize={24}
            fontWeight={700}
            mt={3}
            pl={2}
          >
            Risks
          </Text>
          <Text
            color="neutral.200"
            fontSize={14}
            padding={3}
            backgroundColor="neutral.800"
            borderRadius={8}
            lineHeight={1.4}
          >
            {details.risks}
          </Text>
        </VStack>
      </VStack>
    </Box>
  );
};

function getStrategyDetails(name: string) {
  switch (name) {
    case 'Narrow': {
      return {
        url: '/assets/images/strategies/narrow.svg',
        desc: (
          <>
            Liquidity ranges are automatically rebalanced when certain rebalance
            triggers are hit.
            <br />
            See Illustrative Example above.
            <br />
            The narrower ranges will earn more in fees and rewards, but may
            incur more losses in a high volatility environment.
          </>
        ),
        benefits: (
          <>
            In a low volatility environment, narrower ranges generally earn more
            in fees and perform better due to earning at a higher fee
            multiplier, without suffering much impermanent loss.
            <br />
            Accrued fees will be compounded back into the position on a regular
            basis on behalf of LPs compounding yield.
            <br />
            Auto-rebalancing additionally allows for a passive LP experience.
          </>
        ),
        risks: (
          <>
            In a high volatility environment, narrower ranges may incur more
            impermanent loss and divergence costs.
            <br />
            During periods of prolonged volatility, the higher fees in a
            narrower range may not outweigh the savings in impermanent loss from
            a wider range.
            <br />
            Upon each rebalance, price ranges are set at a fixed range of
            current price, and asset allocations could vary from 50/50.
          </>
        )
      };
    }
    case 'Pegged Price': {
      return {
        url: '/assets/images/strategies/peggedPrice.svg',
        desc: (
          <>
            Liquidity is provided directly around the net asset value of a
            provided asset. For example, a wrapped, staked ETH derivative asset
            will be provided only within a fixed range around its net asset
            value.
          </>
        ),
        benefits:
          'As the net asset value hits certain price targets, the liquidity position will be automatically rebalanced.',
        risks:
          'During times of high volatility in the markets, the actual market price can go out of range.'
      };
    }
    case 'Long-Short': {
      return {
        url: '/assets/images/strategies/long-short.svg',
        desc: (
          <>
            The long-short strategy is a strategy influenced by momentum
            trading. Momentum trading is a technique where price trends
            determine whether to long or short.
            <br />
            When the signal is long, there will be a wider liquidity range above
            the current price. Vice versa, when the signal is short, there will
            be a wider range below the current price.
          </>
        ),
        benefits:
          'Combining liquidity providing with long-short signals from momentum trading can result in better performance in both volatile and stable markets. By providing a wider range where we expect price to go, we can reduce divergence losses during volatile markets by preserving the better performing asset. And when the markets are stable, the strategy is able to benefit from the fees earned by the liquidity position.',
        risks:
          'There can be drawdown periods if, for an extended period of time, the signal is wrong or there is extreme volatility in both directions.'
      };
    }
    case 'Stable':
    default: {
      return {
        url: '/assets/images/strategies/stable.svg',
        desc: 'Liquidity ranges are aimed to straddle 1 at various ranges. Wider ranges will be used for more volatile stablecoin pairs, and narrower ranges will be used for blue chip stablecoin pairs.',
        benefits:
          'We will auto-compound accrued fees back into the liquidity position on a regular basis on behalf of LPs.',
        risks:
          'During times of high volatility in the markets, the allocation of assets could vary quite significantly from 50/50.'
      };
    }
  }
}
